import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import headerTrussImage from '../../../assets/header-truss.jpg';
import { HeaderTruss, TrussRequest } from '../../../types';
import { resolveInsidePostMeasurementForTruss } from '../truss-calculation';
import { INCH_OPTIONS } from '../truss-constants';
import { buildHeaderTrussRequest } from '../truss-request-builder';
import { getTrussAccordionHeader } from '../truss-utils';
import InsidePostMeasurementField from './inside-post-measurement-field';
import {
  isNumericValue,
  MAX_TRUSS_SIZE_FEET,
  validateHeaderTrussWidth,
  validateQuantity,
  validateSizeFeet,
  validateTotalSize,
} from '../truss-validation';

interface HeaderTrussAccordionProps {
  truss: HeaderTruss;
  postSize: string;
  orderDate: string;
  baySpacing?: number;
  onUpdate: (truss: TrussRequest) => void;
  onDelete: () => void;
  defaultExpanded?: boolean;
  hideDeleteButton?: boolean;
}

const HeaderTrussAccordion: React.FC<HeaderTrussAccordionProps> = ({
  truss,
  postSize,
  orderDate,
  baySpacing = 0,
  onUpdate,
  onDelete,
  defaultExpanded = false,
  hideDeleteButton = false,
}) => {
  const [localTruss, setLocalTruss] = React.useState<HeaderTruss>(truss);
  const [errors, setErrors] = React.useState<{
    sizeFeet?: string;
    quantity?: string;
  }>({});
  const [sizeFeetInput, setSizeFeetInput] = React.useState<string>('');
  const [quantityInput, setQuantityInput] = React.useState<string>('');

  // Sync local state when truss prop changes (but don't clear errors - they should persist)
  React.useEffect(() => {
    setLocalTruss(truss);
    // Don't clear errors here - they should persist until user fixes them or blurs
    setSizeFeetInput(truss.sizeFeet?.toString() || '');
    setQuantityInput(truss.quantity?.toString() || '');
  }, [truss]);

  React.useEffect(() => {
    setLocalTruss(prev => {
      const calculatedMeasurement = resolveInsidePostMeasurementForTruss(
        prev.sizeFeet,
        prev.sizeInches,
        'Header',
        postSize
      );
      if (calculatedMeasurement === prev.measurementInsidePost) {
        return prev;
      }
      return { ...prev, measurementInsidePost: calculatedMeasurement };
    });
  }, [localTruss.sizeFeet, localTruss.sizeInches, postSize]);
  const handleFieldChange = (
    field: keyof HeaderTruss,
    value: string | number | boolean | undefined
  ) => {
    const updated = { ...localTruss, [field]: value };

    // Validate numeric fields using functional update to ensure we have latest state
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      if (field === 'sizeFeet' && isNumericValue(value)) {
        const error = validateSizeFeet(value);
        if (error) {
          newErrors.sizeFeet = error;
        } else {
          delete newErrors.sizeFeet;
        }
      } else if (field === 'quantity' && isNumericValue(value)) {
        const error = validateQuantity(value);
        if (error) {
          newErrors.quantity = error;
        } else {
          delete newErrors.quantity;
        }
      }

      if (field === 'sizeFeet' || field === 'sizeInches') {
        const totalError = validateTotalSize(updated.sizeFeet, updated.sizeInches);
        if (totalError) {
          newErrors.sizeFeet = totalError;
        } else {
          const widthError = validateHeaderTrussWidth(
            updated.sizeFeet,
            updated.sizeInches ?? 0,
            baySpacing
          );
          if (widthError) {
            newErrors.sizeFeet = widthError;
          } else if (field === 'sizeInches') {
            delete newErrors.sizeFeet;
          }
        }
      }

      return newErrors;
    });

    const calculatedMeasurement = resolveInsidePostMeasurementForTruss(
      updated.sizeFeet,
      updated.sizeInches,
      'Header',
      postSize
    );
    updated.measurementInsidePost = calculatedMeasurement;
    setLocalTruss(updated);

    // Convert to TrussRequest and notify parent
    const trussRequest = buildHeaderTrussRequest({
      id: truss.id,
      orderDate,
      sizeFeet: updated.sizeFeet,
      sizeInches: updated.sizeInches,
      quantity: updated.quantity,
      measurementInsidePost: updated.measurementInsidePost,
      confirmedMeasurementInsidePost: updated.confirmedMeasurementInsidePost,
      carriageBolts: updated.carriageBolts,
      notes: updated.notes,
      price: updated.price,
    });
    onUpdate(trussRequest);
  };

  const handleSizeFeetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSizeFeetInput(inputValue); // Store raw input for display - this must happen first

    // Allow empty string for user to clear and type freely
    if (inputValue === '') {
      const calculatedMeasurement = resolveInsidePostMeasurementForTruss(
        0,
        localTruss.sizeInches,
        'Header',
        postSize
      );
      const updated = {
        ...localTruss,
        sizeFeet: 0,
        measurementInsidePost: calculatedMeasurement,
      };
      setLocalTruss(updated);
      setErrors(prev => ({ ...prev, sizeFeet: undefined }));
      return;
    }

    // Handle incomplete negative sign
    if (inputValue === '-') {
      setErrors(prev => ({ ...prev, sizeFeet: 'Truss size is required' }));
      return;
    }

    const numValue = parseInt(inputValue, 10);

    // If it's a valid number (including negative), update and validate
    if (!isNaN(numValue)) {
      // Update local state first
      const updated = { ...localTruss, sizeFeet: numValue };
      setLocalTruss(updated);

      const sizeError = validateSizeFeet(numValue);
      const widthError = validateHeaderTrussWidth(numValue, updated.sizeInches ?? 0, baySpacing);
      const combinedError = sizeError ?? widthError;
      if (combinedError) {
        setErrors(prev => ({ ...prev, sizeFeet: combinedError }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.sizeFeet;
          return newErrors;
        });
      }

      const calculatedMeasurement = resolveInsidePostMeasurementForTruss(
        updated.sizeFeet,
        updated.sizeInches,
        'Header',
        postSize
      );
      const updatedWithMeasurement = { ...updated, measurementInsidePost: calculatedMeasurement };
      setLocalTruss(updatedWithMeasurement);

      // Then notify parent
      const trussRequest = buildHeaderTrussRequest({
        id: truss.id,
        orderDate,
        sizeFeet: updatedWithMeasurement.sizeFeet,
        sizeInches: updatedWithMeasurement.sizeInches,
        quantity: updatedWithMeasurement.quantity,
        measurementInsidePost: updatedWithMeasurement.measurementInsidePost,
        confirmedMeasurementInsidePost: updatedWithMeasurement.confirmedMeasurementInsidePost,
        carriageBolts: updatedWithMeasurement.carriageBolts,
        notes: updatedWithMeasurement.notes,
        price: updatedWithMeasurement.price,
      });
      onUpdate(trussRequest);
    } else {
      // If NaN (invalid characters), show required error
      setErrors(prev => ({ ...prev, sizeFeet: 'Truss size is required' }));
    }
  };

  const handleSizeFeetBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.trim();

    // Handle empty or just "-"
    if (inputValue === '' || inputValue === '-') {
      handleFieldChange('sizeFeet', 1);
      setSizeFeetInput('1');
      setErrors(prev => ({ ...prev, sizeFeet: undefined }));
      return;
    }

    const numValue = parseInt(inputValue, 10);

    // Auto-correct invalid values on blur
    if (isNaN(numValue) || numValue < 1) {
      handleFieldChange('sizeFeet', 1);
      setSizeFeetInput('1');
      setErrors(prev => ({ ...prev, sizeFeet: undefined }));
    } else if (numValue > 100) {
      handleFieldChange('sizeFeet', 100);
      setSizeFeetInput('100');
      if (localTruss.sizeInches > 0) {
        handleFieldChange('sizeInches', 0);
      }
      setErrors(prev => ({ ...prev, sizeFeet: undefined }));
    } else if (numValue === MAX_TRUSS_SIZE_FEET && localTruss.sizeInches > 0) {
      handleFieldChange('sizeInches', 0);
      setErrors(prev => ({ ...prev, sizeFeet: undefined }));
    } else {
      handleFieldChange('sizeFeet', numValue);
      setSizeFeetInput(numValue.toString());
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setQuantityInput(inputValue); // Store raw input for display

    // Allow empty string for user to clear and type freely
    if (inputValue === '') {
      const updated = { ...localTruss, quantity: 0 };
      setLocalTruss(updated);
      setErrors(prev => ({ ...prev, quantity: undefined }));
      return;
    }

    // Handle incomplete negative sign
    if (inputValue === '-') {
      setErrors(prev => ({ ...prev, quantity: 'Quantity is required' }));
      return;
    }

    const numValue = parseInt(inputValue, 10);

    // If it's a valid number (including negative), update and validate
    if (!isNaN(numValue)) {
      // Update local state first
      const updated = { ...localTruss, quantity: numValue };
      setLocalTruss(updated);

      // Validate immediately and set error
      const error = validateQuantity(numValue);
      if (error) {
        setErrors(prev => ({ ...prev, quantity: error }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.quantity;
          return newErrors;
        });
      }

      // Then notify parent
      const trussRequest = buildHeaderTrussRequest({
        id: truss.id,
        orderDate,
        sizeFeet: updated.sizeFeet,
        sizeInches: updated.sizeInches,
        quantity: updated.quantity,
        measurementInsidePost: updated.measurementInsidePost,
        confirmedMeasurementInsidePost: updated.confirmedMeasurementInsidePost,
        carriageBolts: updated.carriageBolts,
        notes: updated.notes,
        price: updated.price,
      });
      onUpdate(trussRequest);
    } else {
      setErrors(prev => ({ ...prev, quantity: 'Quantity is required' }));
    }
  };

  const handleQuantityBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.trim();

    // Handle empty or just "-"
    if (inputValue === '' || inputValue === '-') {
      handleFieldChange('quantity', 1);
      setQuantityInput('1');
      setErrors(prev => ({ ...prev, quantity: undefined }));
      return;
    }

    const numValue = parseInt(inputValue, 10);

    // Auto-correct invalid values on blur
    if (isNaN(numValue) || numValue < 1) {
      handleFieldChange('quantity', 1);
      setQuantityInput('1');
      setErrors(prev => ({ ...prev, quantity: undefined }));
    } else if (numValue > 999) {
      handleFieldChange('quantity', 999);
      setQuantityInput('999');
      setErrors(prev => ({ ...prev, quantity: undefined }));
    } else {
      setQuantityInput(numValue.toString());
    }
  };

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        mb: 1,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {getTrussAccordionHeader(localTruss)}
        </Typography>
        {!hideDeleteButton && (
          <IconButton
            size="small"
            color="error"
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{ mr: 1 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth required error={!!errors.sizeFeet}>
                <FormLabel>Truss Size (ft.)</FormLabel>
                <TextField
                  type="number"
                  value={sizeFeetInput}
                  onChange={handleSizeFeetChange}
                  onBlur={handleSizeFeetBlur}
                  inputProps={{ min: 1, max: 100 }}
                  required
                  error={!!errors.sizeFeet}
                />
                {(errors.sizeFeet || !localTruss.sizeFeet || localTruss.sizeFeet === 0) && (
                  <FormHelperText error>
                    {errors.sizeFeet || 'Truss size is required'}
                  </FormHelperText>
                )}
              </FormControl>

              <FormControl fullWidth required>
                <FormLabel>Inches</FormLabel>
                <Select
                  value={localTruss.sizeInches}
                  onChange={e =>
                    handleFieldChange('sizeInches', parseFloat(e.target.value as string))
                  }
                  required
                >
                  {INCH_OPTIONS.map(inch => (
                    <MenuItem key={inch} value={inch}>
                      {inch}"
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required error={!!errors.quantity}>
                <FormLabel>Quantity</FormLabel>
                <TextField
                  type="number"
                  value={quantityInput}
                  onChange={handleQuantityChange}
                  onBlur={handleQuantityBlur}
                  inputProps={{ min: 1, max: 999 }}
                  required
                  error={!!errors.quantity}
                />
                {(errors.quantity || !localTruss.quantity || localTruss.quantity < 1) && (
                  <FormHelperText error>
                    {errors.quantity || 'Quantity is required (minimum 1)'}
                  </FormHelperText>
                )}
              </FormControl>

              <InsidePostMeasurementField
                sizeFeet={localTruss.sizeFeet}
                sizeFeetInput={sizeFeetInput}
                sizeInches={localTruss.sizeInches}
                productType="Header"
                postSize={postSize}
                confirmedMeasurementInsidePost={localTruss.confirmedMeasurementInsidePost}
                onConfirmedChange={checked =>
                  handleFieldChange('confirmedMeasurementInsidePost', checked)
                }
              />
            </Box>
          </Grid>

          {/* Middle Column */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <FormLabel>Carriage Bolts</FormLabel>
                <RadioGroup
                  row
                  value={localTruss.carriageBolts ? 'Yes' : 'No'}
                  onChange={e => handleFieldChange('carriageBolts', e.target.value === 'Yes')}
                >
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                </RadioGroup>
              </FormControl>

              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={6}
                value={localTruss.notes || ''}
                onChange={e => handleFieldChange('notes', e.target.value || undefined)}
                inputProps={{ maxLength: 2000 }}
              />
            </Box>
          </Grid>

          {/* Right Column - Truss Image Only */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                width: '100%',
                height: '100%',
                minHeight: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                overflow: 'hidden',
                p: 2,
              }}
            >
              <Box
                component="img"
                src={headerTrussImage}
                alt="Header Truss"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default HeaderTrussAccordion;
